import { Alert, AlertTitle, Stack } from '@mui/material';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { dismissError } from '../actions/errors.actions';
import type { StoreType } from '../reducers';

// Unchanged from the toasts this component replaced.
const AUTO_HIDE_MS = 3000;

interface ErrorToastProps {
	body: string;
	onClose: () => void;
}

interface ToastListProps {
	errors: StoreType['errors']['list'];
	dismissError: typeof dismissError;
}

const ErrorToast: React.FC<ErrorToastProps> = ({ body, onClose }) => {
	const { t } = useTranslation();
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	// One timer per mounted toast (the list keys each by error id): the list hands
	// down a fresh `onClose` arrow on every render, so reading it through the ref
	// keeps a newly arriving error from restarting the timers already running.
	useEffect(() => {
		const timer = setTimeout(() => onCloseRef.current(), AUTO_HIDE_MS);
		return () => clearTimeout(timer);
	}, []);

	return (
		<Alert severity="error" variant="standard" onClose={onClose}>
			<AlertTitle>{t('Ups, something went wrong')}</AlertTitle>
			{t(body) || t('Please try again')}
		</Alert>
	);
};

const ToastList = (props: ToastListProps) => {
	if (!props.errors.length) {
		return null;
	}

	return (
		<div className="toast-list-container">
			<Stack spacing={1}>
				{props.errors.map((error) => (
					<ErrorToast body={error.message} key={error.id} onClose={() => props.dismissError(error.id)} />
				))}
			</Stack>
		</div>
	);
};

export default compose<React.FC>(
	connect(
		(state: StoreType) => ({
			errors: state.errors.list,
		}),
		{ dismissError }
	)
)(ToastList);
